/**
 * Soft delete for brand fonts.
 *
 * Ads store a font choice as a bare `font-family` STRING and re-resolve it
 * against the live brand profile on every render — there is no `@font-face`
 * or URL captured in the ad. So removing a font entry (or renaming it) breaks
 * every existing ad that references the name, silently: the editor falls back
 * to a default face with no warning anywhere in the chain.
 *
 * The fix is to retire rather than remove:
 *   - the entry stays in `brand_profiles.fonts` (families / files) so
 *     resolution keeps working — nothing on the read path filters on
 *     `deletedAt`, so "a retired font still resolves" is the default,
 *   - the bytes stay in S3 (the delete resolver no longer removes them),
 *   - the entry is hidden only where a human *chooses* a font (brand
 *     settings listings, the Ad Studio picker's option list).
 *
 * **Never splice.** The renderer's font cache key depends on stable array
 * ordering, so every helper here rewrites entries in place and only ever
 * appends.
 *
 * No migration is needed: `brand_profiles_fonts_shape` only CHECKs the
 * top-level shape (`fonts` is an object, `files`/`families` are arrays) and
 * says nothing about array element keys.
 *
 * Pure and dependency-free on purpose — `packages/app` deep-imports this
 * module (not the `@hightouch/lib/brand-profiles` barrel, which pulls in the
 * server logger via `font-entries.ts`).
 */

/** Any font entry that can carry the retirement tag. */
export type RetirableFont = { deletedAt?: string | null };

/** A font family entry, narrowed to what the soft-delete helpers touch. */
export type NamedFontEntry = RetirableFont & {
  id: string;
  name: string;
  isEmailDefault?: boolean;
};

/**
 * True when the entry has been retired. Retired entries still resolve for
 * existing content — they are only hidden from font *pickers* and listings.
 */
export function isRetiredFont(
  entry: RetirableFont | null | undefined,
): boolean {
  return Boolean(entry?.deletedAt);
}

/** Inverse of `isRetiredFont`, for use as an array predicate. */
export function isActiveFont(entry: RetirableFont | null | undefined): boolean {
  return !isRetiredFont(entry);
}

/**
 * Match key for font family names. Ads match the stored family name
 * case-insensitively, so a rename that only changes case is not a rename at
 * all — and a rename back to a previous casing must revive the entry that
 * already owns that name rather than appending a duplicate.
 */
export function fontNameKey(name: string | null | undefined): string {
  return (name ?? "").trim().toLowerCase();
}

/** Drop the retirement tag so the entry shows up in pickers again. */
function revive<T extends RetirableFont>(entry: T): T {
  const { deletedAt: _retired, ...rest } = entry;
  return rest as T;
}

/**
 * Tag `entry` as retired, preserving an existing tag so re-deleting doesn't
 * move the timestamp.
 */
export function retire<T extends RetirableFont>(entry: T, at: string): T {
  return entry.deletedAt ? entry : { ...entry, deletedAt: at };
}

/** A retired family can't stay the email default, so the flag is cleared. */
export function retireFontFamily<T extends NamedFontEntry>(
  family: T,
  at: string,
): T {
  if (isRetiredFont(family)) return family;
  const { isEmailDefault: _default, ...rest } = family;
  return retire(rest as T, at);
}

/**
 * Retire the family with `id`. Returns a new array with the entry rewritten
 * **at its original index** — other entries, and the array length, are
 * untouched.
 */
export function retireFontFamilyById<T extends NamedFontEntry>(
  families: readonly T[],
  id: string,
  at: string,
): T[] {
  return families.map((family) =>
    family.id === id ? retireFontFamily(family, at) : family,
  );
}

/**
 * Retire the file entries at `indices` (positions in `files`). Indices are
 * how the brand-settings UI addresses uploaded files — a file entry has no
 * stable id of its own beyond its storage key.
 */
export function retireFontFilesByIndex<T extends RetirableFont>(
  files: readonly T[],
  indices: Iterable<number>,
  at: string,
): T[] {
  const targets = new Set(indices);
  return files.map((file, index) =>
    targets.has(index) ? retire(file, at) : file,
  );
}

/** Retire the file entry whose `fileId` matches `storageKey`. */
export function retireFontFileByStorageKey<
  T extends RetirableFont & { fileId?: string },
>(files: readonly T[], storageKey: string, at: string): T[] {
  return files.map((file) =>
    file.fileId === storageKey ? retire(file, at) : file,
  );
}

/**
 * Add `next`, reviving a retired entry that already owns the name (matched
 * case-insensitively) instead of appending a second entry under it. Without
 * this, deleting a family and re-adding it by the same name leaves two
 * entries with one `font-family`, and the retired one wins on load order.
 *
 * Only a retired entry is revived, matching `applyFontFamilyEdit` — adding a
 * name an *active* family already holds appends instead of overwriting that
 * family's type, variants and fallback in place.
 */
export function addFontFamily<T extends NamedFontEntry>(
  families: readonly T[],
  next: T,
): T[] {
  const existingIndex = families.findIndex(
    (family) =>
      isRetiredFont(family) &&
      fontNameKey(family.name) === fontNameKey(next.name),
  );
  if (existingIndex === -1) return [...families, revive(next)];
  const result = [...families];
  const existing = result[existingIndex] as T;
  result[existingIndex] = revive({ ...next, id: existing.id });
  return result;
}

/**
 * Apply an edit to the family with `editingId`, handling the rename case.
 *
 * - Name unchanged (case-insensitively): update in place, as before.
 * - Name changed: retire the existing entry **in place** — it keeps its index
 *   and still resolves for ads that reference the old name, but disappears
 *   from listings — then land the new definition. If a *retired* entry already
 *   owns the new name (the rename-back case), that entry is revived in place
 *   instead of appending a duplicate; otherwise the new definition is
 *   appended with `newId`.
 *
 * Only a retired entry is revived. Renaming onto a name an *active* family
 * already holds would otherwise overwrite that family's definition with this
 * form's payload, dropping its type, variants and fallback. The two entries
 * are left to coexist under one name instead — resolution takes the first, and
 * the collision stays visible in the list so a human can fix it.
 *
 * `next` carries the new definition; its `id` and `deletedAt` are ignored in
 * favour of the id of whichever slot the definition lands in.
 */
export function applyFontFamilyEdit<T extends NamedFontEntry>(
  families: readonly T[],
  editingId: string,
  next: T,
  { at, newId }: { at: string; newId: string },
): T[] {
  const editingIndex = families.findIndex((family) => family.id === editingId);
  // The edited family vanished from under us (concurrent delete elsewhere) —
  // leave the array alone rather than resurrecting it at a new index.
  if (editingIndex === -1) return [...families];

  const current = families[editingIndex] as T;
  // An edit, rename included, keeps the typeface the email default.
  const landing: T = current.isEmailDefault
    ? { ...next, isEmailDefault: true }
    : next;
  if (fontNameKey(current.name) === fontNameKey(landing.name)) {
    const updated = [...families];
    updated[editingIndex] = revive({ ...landing, id: editingId });
    return updated;
  }

  const result = families.map((family, index) =>
    index === editingIndex ? retireFontFamily(family, at) : family,
  );
  const reviveIndex = result.findIndex(
    (family, index) =>
      index !== editingIndex &&
      isRetiredFont(family) &&
      fontNameKey(family.name) === fontNameKey(landing.name),
  );
  if (reviveIndex !== -1) {
    const existing = result[reviveIndex] as T;
    result[reviveIndex] = revive({ ...landing, id: existing.id });
    return result;
  }
  result.push(revive({ ...landing, id: newId }));
  return result;
}

/**
 * Match key for uploaded file fonts. Consumers collapse `files` by the
 * extension-stripped basename — that string becomes the CSS `font-family` an
 * ad references — so two rows sharing it are one pickable face, whatever their
 * storage keys.
 */
export function fontFileNameKey(fileName: string | null | undefined): string {
  return fontNameKey((fileName ?? "").replace(/\.[^.]+$/, ""));
}

/**
 * Add uploaded `files`, reviving retired rows that already own an incoming
 * name instead of appending alongside them.
 *
 * Without this, re-uploading a font you previously deleted leaves both rows in
 * the array under one `font-family`. Consumers collapse by name keeping the
 * first-seen row of the best format, so the retired row — earlier in the array
 * and usually the same format — wins, and the picker's new entry renders the
 * old glyphs.
 *
 * A revived row is replaced wholesale by the incoming one (new storage key,
 * new parsed metadata) at its original index; only genuinely new names are
 * appended.
 */
export function addFontFiles<T extends RetirableFont & { fileName?: string }>(
  files: readonly T[],
  next: readonly T[],
): T[] {
  const result = [...files];
  for (const file of next) {
    const key = fontFileNameKey(file.fileName);
    const existingIndex = key
      ? result.findIndex(
          (candidate) =>
            isRetiredFont(candidate) &&
            fontFileNameKey(candidate.fileName) === key,
        )
      : -1;
    if (existingIndex === -1) {
      result.push(revive(file));
    } else {
      result[existingIndex] = revive(file);
    }
  }
  return result;
}
